import { useIntl } from 'react-intl'

import { AxiosError } from 'axios'
import { v4 as uuid } from 'uuid'

import { useAppDispatch } from '../../../data/store'
import { setToastContent } from '../../data/ltToastAutoActions'
import {
  useDuplicateNpTestPlanContactGroupMutation,
  useRemoveNpTestPlanContactGroupMutation,
} from '../data/npTestPlansApi'

export const useNpTestPlanContactGroups = (planId?: string | null) => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const [duplicate, { isLoading: isDuplicating }] = useDuplicateNpTestPlanContactGroupMutation()
  const [remove, { isLoading: isRemoving }] = useRemoveNpTestPlanContactGroupMutation()

  const showError = (error: unknown, fallback: string, id: string) =>
    dispatch(
      setToastContent({
        text: (error as AxiosError<{ message?: string }>)?.response?.data?.message || fallback,
        toastType: 'error',
        id,
      }),
    )

  const duplicateGroup = async (sourceGroupId: string) => {
    if (!planId) {
      return
    }
    try {
      await duplicate({ planId, sourceGroupId, contactGroupId: uuid() }).unwrap()
    } catch (error) {
      showError(
        error,
        formatMessage({
          defaultMessage: "The group couldn't be duplicated. Please try again.",
          description: 'Toast error shown when duplicating a group of test measures on a neuropsych test plan fails',
        }),
        'NpTestPlan-duplicateGroup-error',
      )
    }
  }

  const removeGroup = async (contactGroupId: string) => {
    if (!planId) {
      return
    }
    try {
      await remove({ planId, contactGroupId }).unwrap()
    } catch (error) {
      showError(
        error,
        formatMessage({
          defaultMessage: "The group couldn't be removed. Please try again.",
          description: 'Toast error shown when removing a group of test measures from a neuropsych test plan fails',
        }),
        'NpTestPlan-removeGroup-error',
      )
    }
  }

  return { duplicateGroup, removeGroup, isSaving: isDuplicating || isRemoving }
}
